import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Shield, Save, RotateCcw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MODULE_HINTS = {
  dashboard: 'view = Show Remaining Amount on Recovery / Sales dashboards',
};

export default function PermissionMatrixDialog({ open, onOpenChange, user }) {
  const [modules, setModules] = useState([]);
  const [actions, setActions] = useState([]);
  const [matrix, setMatrix] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !user) return;
    Promise.all([
      axios.get(`${API_URL}/api/users/permissions/modules`, { withCredentials: true }),
      axios.get(`${API_URL}/api/users/permissions/defaults/${encodeURIComponent(user.role)}`, { withCredentials: true }),
    ]).then(([m, d]) => {
      setModules(m.data.modules);
      setActions(m.data.actions);
      const current = user.permissions && Object.keys(user.permissions || {}).length ? user.permissions : d.data;
      setMatrix(current || {});
    }).catch(() => toast.error('Failed to load permissions'));
  }, [open, user]);

  const toggle = (module, action) => {
    setMatrix((prev) => ({
      ...prev,
      [module]: { ...(prev[module] || {}), [action]: !prev[module]?.[action] },
    }));
  };

  const resetToDefaults = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/users/permissions/defaults/${encodeURIComponent(user.role)}`, { withCredentials: true });
      setMatrix(data || {});
      toast.info('Reset to role defaults');
    } catch { toast.error('Failed to reset'); }
  };

  const save = async () => {
    try {
      setSaving(true);
      await axios.put(`${API_URL}/api/users/${user.id}/permissions`, matrix, { withCredentials: true });
      toast.success('Permissions updated');
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center space-x-2">
            <Shield className="h-5 w-5 text-amber-500" />
            <span>Permissions — {user?.name} <span className="text-amber-500 text-sm ml-2">({user?.role})</span></span>
          </DialogTitle>
          <DialogDescription className="text-slate-400">Fine-tune what this employee can do across every module.</DialogDescription>
        </DialogHeader>

        <Table>
          <TableHeader>
            <TableRow className="border-slate-800">
              <TableHead className="text-slate-400">Module</TableHead>
              {actions.map((a) => (
                <TableHead key={a} className="text-slate-400 capitalize text-center">{a}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {modules.map((m) => (
              <TableRow key={m} className="border-slate-800" data-testid={`perm-row-${m}`}>
                <TableCell className="text-white capitalize font-medium">
                  {m.replace(/_/g, ' ')}
                  {MODULE_HINTS[m] && (
                    <div className="text-xs text-amber-500/80 mt-1 normal-case font-normal">
                      {MODULE_HINTS[m]}
                    </div>
                  )}
                </TableCell>
                {actions.map((a) => (
                  <TableCell key={a} className="text-center">
                    <Checkbox
                      checked={!!matrix[m]?.[a]}
                      onCheckedChange={() => toggle(m, a)}
                      data-testid={`perm-${m}-${a}`}
                      className="border-slate-600 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
                    />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <div className="flex justify-between pt-4 border-t border-slate-800">
          <Button variant="ghost" onClick={resetToDefaults} className="text-slate-300" data-testid="reset-permissions">
            <RotateCcw className="h-4 w-4 mr-2" /> Reset to Role Defaults
          </Button>
          <div className="flex space-x-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-300">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-amber-500 hover:bg-amber-600" data-testid="save-permissions">
              <Save className="h-4 w-4 mr-2" /> {saving ? 'Saving...' : 'Save Permissions'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
